import { UNSAFE_PortalProvider } from '@react-aria/overlays';
import { ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@shared/util/classnames';
import styles from './HeroPortal.module.css';

interface HeroPortalProps {
  children: ReactNode;
  /**
   * Put the host in the browser top layer. Required when the surface being wrapped is itself in
   * the top layer (`Menu.Content`, `AnchoredView`), since nothing below it can paint above it.
   * Otherwise the host is portaled to `<body>`.
   */
  topLayer?: boolean;
  /** Applied to the host, for theme or scoping classes its contents need. */
  className?: string;
}

/**
 * Redirects the react-aria overlays rendered by `children` — HeroUI `DatePicker` calendars,
 * `Select` listboxes, `Tooltip`s — into a host that carries the CSS resets their styling assumes,
 * and that can sit in the top layer. The host renders as a sibling of `children`.
 */
export function HeroPortal({ children, topLayer = false, className }: HeroPortalProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  // Opened on demand: the top layer stacks by `showPopover()` order, so the host has to open
  // after the surface around it.
  const getContainer = () => {
    const host = hostRef.current;
    if (topLayer && host?.isConnected && !host.matches(':popover-open')) {
      host.showPopover();
    }
    return host;
  };

  const host = (
    <div
      ref={hostRef}
      // Exempts the host from `Dialog`'s background inerting.
      data-hero-portal
      popover={topLayer ? 'manual' : undefined}
      className={cn(styles.portalHost, topLayer && styles.topLayer, className)}
    />
  );

  return (
    <>
      <UNSAFE_PortalProvider getContainer={getContainer}>{children}</UNSAFE_PortalProvider>
      {topLayer ? host : createPortal(host, document.body)}
    </>
  );
}
